import React from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import { ChartOptions, ChartData } from "chart.js";

import { sales_colors } from "./colors";
import { DashboardData, TypeBreakdown } from "../../../models/sales_model";
import { formatNumber } from "../../../middleware/common-functions";
import { lang } from "../../../middleware/lang";

type ChartsProps = {
  dashboard: DashboardData;
  isMultiDay: boolean;
};

const deliveryLabels: Record<string, string> = {
  delivery: lang("A domicilio", "Delivery"),
  pickup: lang("Para Recoger", "Pickup"),
  for_here: lang("En Comedor", "For Here"),
  other_apps: lang("Otras Plataformas", "Other Apps"),
};

const paymentLabels: Record<string, string> = {
  cash: lang("Efectivo", "Cash"),
  online: "Online",
  card_pos: "Foo Pay",
  terminal: "Terminal",
  other_apps: lang("Otras Plataformas", "Other Apps"),
};

const doughnutOptions: ChartOptions<"doughnut"> = {
  plugins: {
    legend: {
      position: "bottom",
      display: false,
    },
  },
};

const currencyTick = {
  callback: (value: any) => `$${Number(value).toLocaleString()}`,
};

const barOptions: ChartOptions<"bar"> = {
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: {
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
      ticks: currencyTick,
    },
  },
};

const lineOptions: ChartOptions<"line"> = {
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: {
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
      ticks: currencyTick,
    },
  },
};

const border_colors = ["rgba(142, 168, 195, 0)"];

const Charts: React.FC<ChartsProps> = ({ dashboard, isMultiDay }) => {
  const { byDeliveryType, byPaymentType, hourlyBreakdown, dailyBreakdown } =
    dashboard;

  // Delivery doughnut
  const deliveryData: ChartData<"doughnut"> = {
    labels: byDeliveryType.map((d) => deliveryLabels[d.type] || d.type),
    datasets: [
      {
        label: lang("Forma de Entrega", "Delivery Type"),
        data: byDeliveryType.map((d) => d.total),
        backgroundColor: byDeliveryType.map(
          (d) => (sales_colors as any)[d.type] || "#ccc"
        ),
        borderColor: byDeliveryType.map(() => border_colors[0]),
        borderWidth: 1,
      },
    ],
  };

  // Payment doughnut
  const paymentData: ChartData<"doughnut"> = {
    labels: byPaymentType.map((p) => paymentLabels[p.type] || p.type),
    datasets: [
      {
        label: lang("Forma de Pago", "Payment Method"),
        data: byPaymentType.map((p) => p.total),
        backgroundColor: byPaymentType.map(
          (p) => (sales_colors as any)[p.type] || "#ccc"
        ),
        borderColor: byPaymentType.map(() => border_colors[0]),
        borderWidth: 1,
      },
    ],
  };

  // Hourly bar chart
  const hourlyLabels = hourlyBreakdown.map((h) => `${h.hour}:00`);
  const hourlyData: ChartData<"bar"> = {
    labels: hourlyLabels,
    datasets: [
      {
        label: lang("Ventas por Hora", "Hourly Sales"),
        data: hourlyBreakdown.map((h) => h.total),
        backgroundColor: hourlyBreakdown.map(() => sales_colors.delivery),
        borderColor: hourlyBreakdown.map(() => sales_colors.delivery),
        borderWidth: 1,
      },
    ],
  };

  // Daily trend line/bar chart
  const dailyData: ChartData<"line"> = {
    labels: dailyBreakdown.map((d) => d.date),
    datasets: [
      {
        label: lang("Ventas Diarias", "Daily Sales"),
        data: dailyBreakdown.map((d) => d.total),
        borderColor: sales_colors.pickup,
        backgroundColor: "rgba(62, 114, 175, 0.1)",
        fill: true,
        tension: 0.3,
      },
    ],
  };

  return (
    <div className="charts-section mt-3">
      {/* Row 1: Doughnut charts */}
      <div className="row charts">
        <div className="col-md-6 mb-3">
          <div className="material-shadows chart-item p-3">
            <h6 className="text-muted mb-3">
              {lang("Ventas por Tipo de Entrega", "Sales by Delivery Type")}
            </h6>
            <div className="row justify-content-center">
              <div className="col-8 col-md-6">
                <Doughnut type="doughnut" data={deliveryData} options={doughnutOptions} />
              </div>
            </div>
            <BreakdownTable
              data={byDeliveryType}
              labels={deliveryLabels}
            />
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <div className="material-shadows chart-item p-3">
            <h6 className="text-muted mb-3">
              {lang("Ventas por Metodo de Pago", "Sales by Payment Method")}
            </h6>
            <div className="row justify-content-center">
              <div className="col-8 col-md-6">
                <Doughnut type="doughnut" data={paymentData} options={doughnutOptions} />
              </div>
            </div>
            <BreakdownTable
              data={byPaymentType}
              labels={paymentLabels}
            />
          </div>
        </div>
      </div>

      {/* Row 2: Time-based charts */}
      <div className="row charts">
        <div className={isMultiDay ? "col-md-6 mb-3" : "col-12 mb-3"}>
          <div className="material-shadows chart-item p-3">
            <h6 className="text-muted mb-3">
              {lang("Ventas por Hora", "Hourly Sales")}
            </h6>
            <Bar type="bar" data={hourlyData} options={barOptions} />
          </div>
        </div>
        {isMultiDay && dailyBreakdown.length > 1 && (
          <div className="col-md-6 mb-3">
            <div className="material-shadows chart-item p-3">
              <h6 className="text-muted mb-3">
                {lang("Tendencia Diaria", "Daily Trend")}
              </h6>
              <Line type="line" data={dailyData} options={lineOptions} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type BreakdownTableProps = {
  data: TypeBreakdown[];
  labels: Record<string, string>;
};

const BreakdownTable: React.FC<BreakdownTableProps> = ({ data, labels }) => (
  <table className="table table-borderless table-hover mt-3 table-sm">
    <thead>
      <tr>
        <th scope="col">{lang("Tipo", "Type")}</th>
        <th scope="col">{lang("Ordenes", "Orders")}</th>
        <th scope="col">{lang("Ingreso", "Revenue")}</th>
      </tr>
    </thead>
    <tbody>
      {data
        .filter((item) => item.count > 0)
        .map((item) => (
          <tr key={item.type}>
            <td className="position-relative pl-5 text-left">
              <div
                className="reports-square absolute"
                style={{
                  backgroundColor:
                    (sales_colors as any)[item.type] || "#ccc",
                }}
              ></div>
              {labels[item.type] || item.type}
            </td>
            <td>{item.count}</td>
            <td>${formatNumber(item.total)}</td>
          </tr>
        ))}
    </tbody>
  </table>
);

export default Charts;
